// Public Project View - Professionally formatted project details
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Clock,
  Award,
  Tag,
  Download,
  ChevronLeft,
  ChevronRight,
  Package,
  Lightbulb,
  Palette,
  BookOpen,
  FileText
} from 'lucide-react';
import PortalLayout from './PortalLayout';

const PublicProject = () => {
  const { teacherId, projectId } = useParams();
  const [project, setProject] = useState(null);
  const [teacherInfo, setTeacherInfo] = useState(null);
  const [extractedPortalSettings, setExtractedPortalSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  useEffect(() => {
    fetchProjectData();
  }, [teacherId, projectId]);

  const fetchProjectData = async () => {
    try {
      setLoading(true);

      // Fetch teacher info
      const teacherResponse = await fetch(`/api/portal/${teacherId}/teacher`);
      if (!teacherResponse.ok) throw new Error('Failed to fetch teacher info');
      const teacherData = await teacherResponse.json();
      const teacher = teacherData.teacher;
      setTeacherInfo(teacher);

      // Extract portal settings
      const portalSettings = {
        logo: teacher.logo,
        logoSize: teacher.logoSize || 'medium',
        colorScheme: teacher.colorScheme || 'craft',
        businessName: teacher.businessName,
        tagline: teacher.tagline,
        contactEmail: teacher.contactEmail,
        contactPhone: teacher.contactPhone,
        address: teacher.address,
        welcomeMessage: teacher.welcomeMessage
      };
      setExtractedPortalSettings(portalSettings);

      // Fetch project details
      const projectResponse = await fetch(`/api/portal/${teacherId}/project/${projectId}`);
      if (!projectResponse.ok) throw new Error('Failed to fetch project');
      const projectData = await projectResponse.json();
      setProject(projectData);

      setLoading(false);
    } catch (err) {
      console.error('Error fetching project:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <PortalLayout teacherInfo={teacherInfo} portalSettings={extractedPortalSettings}>
        <div className="flex justify-center items-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
        </div>
      </PortalLayout>
    );
  }

  if (error || !project) {
    return (
      <PortalLayout teacherInfo={teacherInfo} portalSettings={extractedPortalSettings}>
        <div className="container mx-auto px-4 py-12 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Project Not Found</h2>
          <p className="text-gray-600">The project you're looking for doesn't exist or is not available.</p>
        </div>
      </PortalLayout>
    );
  }

  const allImages = [
    project.main_image_url,
    ...(project.images || []).map(img => img.image_url)
  ].filter(Boolean);

  const nextImage = () => {
    setSelectedImageIndex((prev) => (prev + 1) % allImages.length);
  };

  const prevImage = () => {
    setSelectedImageIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
  };

  return (
    <PortalLayout teacherInfo={teacherInfo} portalSettings={extractedPortalSettings}>
      <div className="container mx-auto px-4 py-8" style={{ fontFamily: "'Inter', sans-serif" }}>
        {/* Header Section */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">{project.name}</h1>
        </div>

        {/* Image and Description Container */}
        <div className="mb-8 bg-white rounded-lg shadow-md overflow-hidden p-6">
          <div className="flex flex-col sm:flex-row gap-6">
            {/* Image Gallery */}
            {allImages.length > 0 && (
              <div className="flex-shrink-0 w-full sm:w-96">
                <div className="relative">
                  <img
                    src={allImages[selectedImageIndex]}
                    alt={project.name}
                    className="w-full h-auto rounded-lg shadow-sm"
                  />
                  {allImages.length > 1 && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg transition-colors"
                      >
                        <ChevronLeft className="w-6 h-6" />
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg transition-colors"
                      >
                        <ChevronRight className="w-6 h-6" />
                      </button>
                      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                        {selectedImageIndex + 1} / {allImages.length}
                      </div>
                    </>
                  )}
                </div>
                {allImages.length > 1 && (
                  <div className="mt-4 flex gap-2 overflow-x-auto">
                    {allImages.map((img, idx) => (
                      <button
                        key={idx}
                        onClick={() => setSelectedImageIndex(idx)}
                        className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                          idx === selectedImageIndex ? 'border-pink-500' : 'border-gray-300'
                        }`}
                      >
                        <img src={img} alt={`View ${idx + 1}`} className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Description */}
            {project.description && (
              <div className="flex-1">
                <h2 className="text-xl font-bold text-gray-900 mb-4">About This Project</h2>

                {/* Project Meta Info Badges */}
                <div className="flex flex-wrap gap-4 text-sm mb-4">
                  {project.difficulty && (
                    <div className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-800 rounded-full">
                      <Award className="w-4 h-4" />
                      <span className="font-medium">{project.difficulty}</span>
                    </div>
                  )}
                  {project.category && (
                    <div className="flex items-center gap-2 px-3 py-1 bg-purple-100 text-purple-800 rounded-full">
                      <Tag className="w-4 h-4" />
                      <span className="font-medium">{project.category}</span>
                    </div>
                  )}
                  {project.estimated_time && (
                    <div className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-800 rounded-full">
                      <Clock className="w-4 h-4" />
                      <span className="font-medium">{project.estimated_time}</span>
                    </div>
                  )}
                </div>

                <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{project.description}</p>
              </div>
            )}
          </div>
        </div>

        {/* Materials and Instructions Side by Side */}
        <div className="mb-8 grid lg:grid-cols-3 gap-8 lg:items-start">
          {/* Materials & Supplies - Left Column (1/3 width) */}
          <div className="space-y-6 lg:h-full flex flex-col">
            {project.materials && project.materials.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6 flex-1">
                <div className="flex items-center gap-2 mb-4">
                  <Package className="w-5 h-5 text-pink-500" />
                  <h3 className="text-xl font-bold text-gray-900">Materials & Supplies</h3>
                </div>
                <ul className="space-y-2">
                  {project.materials.map((material, idx) => (
                    <li key={idx} className="flex items-start text-gray-700">
                      <span className="text-pink-500 mr-2 mt-1">•</span>
                      <span>{material}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Project Files */}
            {project.files && project.files.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center gap-2 mb-4">
                  <FileText className="w-5 h-5 text-pink-500" />
                  <h3 className="text-xl font-bold text-gray-900">Project Files</h3>
                </div>
                <div className="space-y-2">
                  {project.files.map((file, idx) => (
                    <a
                      key={idx}
                      href={file.file_url}
                      download
                      className="flex items-center gap-2 p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors group"
                    >
                      <Download className="w-4 h-4 text-gray-500 group-hover:text-pink-500" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{file.file_name}</p>
                        {file.file_size && (
                          <p className="text-xs text-gray-500">
                            {(file.file_size / 1024).toFixed(0)} KB
                          </p>
                        )}
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Instructions - Right Column (2/3 width) */}
          <div className="lg:col-span-2 space-y-8 lg:h-full flex flex-col">
            {/* Instructions */}
            {project.instructions && (
              <section className="bg-white rounded-lg shadow-md p-6 flex-1">
                <div className="flex items-center gap-2 mb-4">
                  <BookOpen className="w-6 h-6 text-pink-500" />
                  <h2 className="text-xl font-bold text-gray-900">Step-by-Step Instructions</h2>
                </div>
                <div className="prose max-w-none text-gray-700 leading-relaxed whitespace-pre-wrap">
                  {project.instructions}
                </div>
              </section>
            )}

            {/* Pro Tips */}
            {project.tips && (
              <section className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-lg shadow-md p-6 border-l-4 border-yellow-400">
                <div className="flex items-center gap-2 mb-4">
                  <Lightbulb className="w-6 h-6 text-yellow-600" />
                  <h2 className="text-xl font-bold text-gray-900">Pro Tips & Advice</h2>
                </div>
                <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{project.tips}</p>
              </section>
            )}

            {/* Variations */}
            {project.variations && (
              <section className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg shadow-md p-6 border-l-4 border-purple-400">
                <div className="flex items-center gap-2 mb-4">
                  <Palette className="w-6 h-6 text-purple-600" />
                  <h2 className="text-xl font-bold text-gray-900">Variations & Creative Ideas</h2>
                </div>
                <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{project.variations}</p>
              </section>
            )}
          </div>
        </div>
      </div>
    </PortalLayout>
  );
};

export default PublicProject;
